/**
 * SC variant picker: full document navigation (no DOM replacement).
 * Prefetch + in-memory dedupe warms HTTP cache; parsed HTML preloads priority images.
 */
(function () {
  var MAX_CACHE_ENTRIES = 48;
  var MAX_IMAGES_WARM = 48;
  var pageHtmlCache = new Map();
  var prefetchHints = new Set();

  function pageCacheKey(url) {
    return url.origin + url.pathname + url.search;
  }

  function shouldSkipPreload(productUrl) {
    if (window.Shopify && window.Shopify.designMode) return true;
    if (productUrl.origin !== window.location.origin) return true;
    if (!productUrl.pathname.includes("/products/")) return true;
    var here = new URL(window.location.href);
    if (
      productUrl.pathname === here.pathname &&
      productUrl.search === here.search
    ) {
      return true;
    }
    return false;
  }

  function absolutizeUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (e) {
      return "";
    }
  }

  function addSrcsetUrls(set, srcset) {
    if (!srcset) return;
    srcset.split(",").forEach(function (part) {
      var u = part.trim().split(/\s+/)[0];
      var abs = absolutizeUrl(u);
      if (abs) set.add(abs);
    });
  }

  function collectImageUrlsFromDocument(doc) {
    var urls = new Set();
    var count = 0;
    doc
      .querySelectorAll("img[src], img[srcset], picture source[srcset]")
      .forEach(function (node) {
        if (count >= MAX_IMAGES_WARM) return;
        if (node.tagName === "IMG" && node.src) {
          var s = absolutizeUrl(node.src);
          if (s) {
            urls.add(s);
            count++;
          }
          addSrcsetUrls(urls, node.getAttribute("srcset"));
        } else if (node.tagName === "SOURCE") {
          addSrcsetUrls(urls, node.getAttribute("srcset"));
        }
      });
    return urls;
  }

  function preloadImageUrl(url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () {
        resolve();
      };
      img.onerror = function () {
        resolve();
      };
      img.src = url;
    });
  }

  function warmImagesFromHtml(html) {
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      collectImageUrlsFromDocument(doc).forEach(function (url) {
        preloadImageUrl(url);
      });
    } catch (e) {
      /* ignore */
    }
  }

  function injectPrefetchLink(absUrl) {
    if (prefetchHints.has(absUrl)) return;
    prefetchHints.add(absUrl);
    var link = document.createElement("link");
    link.rel = "prefetch";
    link.href = absUrl;
    link.setAttribute("as", "document");
    document.head.appendChild(link);
  }

  /**
   * Fetch full product page HTML (same URL the user navigates to). Dedupes in-flight
   * requests; warms browser cache + decodes images from markup.
   */
  function prefetchProductPage(href) {
    var productUrl = new URL(href, window.location.origin);
    if (shouldSkipPreload(productUrl)) return;

    var key = pageCacheKey(productUrl);
    if (pageHtmlCache.has(key)) {
      return pageHtmlCache.get(key);
    }

    injectPrefetchLink(key);

    var promise = fetch(key, { credentials: "same-origin", redirect: "follow" })
      .then(function (response) {
        if (!response.ok) throw new Error("prefetch failed");
        return response.text();
      })
      .then(function (text) {
        warmImagesFromHtml(text);
        return text;
      })
      .catch(function () {
        pageHtmlCache.delete(key);
      });

    if (pageHtmlCache.size >= MAX_CACHE_ENTRIES) {
      var firstKey = pageHtmlCache.keys().next().value;
      pageHtmlCache.delete(firstKey);
    }
    pageHtmlCache.set(key, promise);
    return promise;
  }

  function preloadIfNeeded(link) {
    if (!link) return;
    prefetchProductPage(link.getAttribute("href"));
  }

  var OVERLAY_ID = "sc-product-section-switch-overlay";

  function showNavLoadingOverlay() {
    if (document.getElementById(OVERLAY_ID)) return;
    var overlay = document.createElement("div");
    overlay.id = OVERLAY_ID;
    overlay.className =
      "sc-product-section-switch__overlay sc-product-section-switch__overlay--page";
    overlay.setAttribute("aria-hidden", "true");
    var spinner = document.createElement("div");
    spinner.className = "sc-product-section-switch__spinner";
    spinner.setAttribute("role", "status");
    overlay.appendChild(spinner);
    document.body.appendChild(overlay);
    document.body.classList.add("sc-product-section-switch--navigating");
    document.body.setAttribute("aria-busy", "true");
  }

  function removeNavLoadingOverlay() {
    var el = document.getElementById(OVERLAY_ID);
    if (el) el.remove();
    document.body.classList.remove("sc-product-section-switch--navigating");
    document.body.removeAttribute("aria-busy");
  }

  window.addEventListener("pageshow", function (event) {
    if (event.persisted) removeNavLoadingOverlay();
  });

  document.body.addEventListener(
    "click",
    function (event) {
      var link = event.target.closest(".sc-variant-picker__link");
      if (!link) return;
      var root = link.closest(".sc-variant-picker[data-sc-section-id]");
      if (!root) return;

      if (window.Shopify && window.Shopify.designMode) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;

      var nextUrl = new URL(link.getAttribute("href"), window.location.origin);
      var here = new URL(window.location.href);
      if (nextUrl.origin !== window.location.origin) return;
      if (!nextUrl.pathname.includes("/products/")) return;
      if (nextUrl.pathname === here.pathname && nextUrl.search === here.search)
        return;

      event.preventDefault();
      showNavLoadingOverlay();
      var href = link.href;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          window.location.assign(href);
        });
      });
    },
    true,
  );

  function onPickerLinkEvent(event) {
    var link = event.target.closest(".sc-variant-picker__link");
    if (!link) return;
    if (!link.closest(".sc-variant-picker[data-sc-section-id]")) return;
    preloadIfNeeded(link);
  }

  document.body.addEventListener("pointerdown", onPickerLinkEvent, true);
  document.body.addEventListener("mouseover", onPickerLinkEvent, true);
  document.body.addEventListener("focusin", onPickerLinkEvent, true);
  document.body.addEventListener("touchstart", onPickerLinkEvent, {
    capture: true,
    passive: true,
  });

  var idlePreload = function (root) {
    root.querySelectorAll(".sc-variant-picker__link").forEach(function (link) {
      preloadIfNeeded(link);
    });
  };

  if ("IntersectionObserver" in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          var run = function () {
            idlePreload(entry.target);
          };
          if (window.requestIdleCallback) {
            window.requestIdleCallback(run, { timeout: 2500 });
          } else {
            setTimeout(run, 0);
          }
        });
      },
      { rootMargin: "100px" },
    );

    document
      .querySelectorAll(".sc-variant-picker[data-sc-section-id]")
      .forEach(function (el) {
        io.observe(el);
      });
  }
})();
